import { useEffect, useState } from 'react';
import { api } from './api.js';

/**
 * Opening an incident.
 *
 * The two fields that matter are not the name and the reference — they are the structure
 * and the plans. An incident created without them has no positions, therefore no
 * checklists, therefore nothing to do; it opens onto an empty board and looks broken.
 *
 * So both are offered, both default to something sensible, and neither is magic: the
 * structure is copied from an incident you can see and name, rather than from a hidden
 * template that drifts out of step with the incidents actually run from it.
 */

const today = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
};

export function NewIncident({ onClose, onCreated }) {
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState({
    ref: `INC-${today()}`, name: '', mode: 'exercise', situation: '', copyStructureFrom: '',
  });
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.newIncidentOptions().then((o) => {
      setOptions(o);
      // Default to the most recent structure and every approved base plan. The common
      // case by a wide margin is "run this one like the last one", and making that the
      // default is the difference between four clicks and a shrug.
      setForm((f) => ({ ...f, copyStructureFrom: o.sources[0]?.id ?? '' }));
      setPlans(o.plans.filter((p) => p.kind === 'base').map((p) => p.plan_version_id));
    }).catch(() => setError('Could not load the plans and structures.'));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const togglePlan = (id) =>
    setPlans((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const { incident } = await api.createIncident({
        ref: form.ref.trim(), name: form.name.trim(), mode: form.mode,
        situation: form.situation.trim(),
        copyStructureFrom: form.copyStructureFrom || null,
        planVersionIds: plans,
      });
      onCreated(incident);
    } catch (err) {
      setError(err.code === 'invalid_request'
        ? (err.detail || 'Check the reference and name.')
        : err.status === 403 ? 'Your role cannot open an incident.'
        : 'That did not save. The reference may already be in use.');
      setBusy(false);
    }
  }

  const selectedSource = options?.sources.find((s) => s.id === form.copyStructureFrom);

  return (
    <div className="hoWrap" onClick={onClose}>
      <div className="ho" onClick={(e) => e.stopPropagation()}>
        <div className="hoHead">
          <div>
            <h3>Open an incident</h3>
            <p>It starts running as soon as you save, and the log starts with it</p>
          </div>
          <button className="hoX" onClick={onClose} aria-label="Close">×</button>
        </div>

        <form className="hoBody form" onSubmit={submit}>
          <div className="row3">
            <label className="f narrow">
              <span>Reference</span>
              <input value={form.ref} onChange={set('ref')} maxLength={40} required />
            </label>
            <label className="f">
              <span>Name</span>
              <input value={form.name} onChange={set('name')} maxLength={200} required autoFocus
                     placeholder="Process release, north separator" />
            </label>
          </div>

          <label className="f">
            <span>Situation as it stands (optional)</span>
            <textarea value={form.situation} onChange={set('situation')} rows={2} maxLength={4000}
                      placeholder="What is known right now. It can be changed later." />
          </label>

          <label className="f">
            <span>Command structure</span>
            <select value={form.copyStructureFrom} onChange={set('copyStructureFrom')}>
              <option value="">Start with no positions</option>
              {options?.sources.map((s) => (
                <option key={s.id} value={s.id}>
                  Same as {s.ref} — {s.name} ({s.positions} positions)
                </option>
              ))}
            </select>
            <small>
              {selectedSource
                ? `${selectedSource.positions} positions copied. Nobody is put in a seat — each one starts unheld.`
                : 'Without positions there is nowhere for checklists to go. You can still add them later.'}
            </small>
          </label>

          {options && options.plans.length > 0 && (
            <div className="f">
              <span>Plans to apply</span>
              <div className="checks">
                {options.plans.map((p) => (
                  <label key={p.plan_version_id} className="check">
                    <input type="checkbox" checked={plans.includes(p.plan_version_id)}
                           onChange={() => togglePlan(p.plan_version_id)} />
                    <span>{p.name} <em>v{p.version} · {p.items} actions</em></span>
                  </label>
                ))}
              </div>
              <small>Layers add. Applying a second plan never removes what the first one put there.</small>
            </div>
          )}

          <label className="f">
            <span>This is</span>
            <select value={form.mode} onChange={set('mode')}>
              <option value="exercise">An exercise</option>
              <option value="live">A live incident</option>
            </select>
            {form.mode === 'live'
              ? <small className="warnNote">
                  Live cannot be changed to exercise afterwards, and exercise controls are
                  refused against it. Choose exercise if this is a drill.
                </small>
              : <small>Marked as an exercise everywhere it appears, permanently.</small>}
          </label>

          {error && <p className="err">{error}</p>}

          <div className="actions">
            <span className="spacer" />
            <button type="button" className="ghost" onClick={onClose}>Cancel</button>
            <button className="btn narrow" type="submit" disabled={busy || !form.name.trim()}>
              {busy ? 'Opening…' : 'Open incident'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
